import type { NotificationUnreadCount } from "@facade/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { notificationsApi } from "@facade/services";
import { notificationQueryKeys } from "../queries";
import type { Project } from "@facade/contracts";
import { projectQueryKeys } from "@facade/features/projects/queries";
import {
  adjustProjectUnreadNotificationCount,
  updateNotificationLists,
} from "./notificationCache";

export function useMarkNotificationReadMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationsApi.markRead,
    onMutate: async (notificationId: string) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: notificationQueryKeys.all }),
        queryClient.cancelQueries({
          queryKey: projectQueryKeys.all,
          exact: true,
        }),
      ]);
      const previousLists = queryClient.getQueriesData({
        queryKey: notificationQueryKeys.lists(),
      });
      const previousCount = queryClient.getQueryData<NotificationUnreadCount>(
        notificationQueryKeys.unreadCount(),
      );
      const previousProjects = queryClient.getQueryData<Project[]>(
        projectQueryKeys.all,
      );
      let wasUnread = false;
      let projectId: string | null = null;

      updateNotificationLists(queryClient, (notification) => {
        if (notification.id !== notificationId || notification.readAt) {
          return notification;
        }
        wasUnread = true;
        projectId = notification.projectId;
        return { ...notification, readAt: new Date().toISOString() };
      });

      if (wasUnread && previousCount) {
        queryClient.setQueryData<NotificationUnreadCount>(
          notificationQueryKeys.unreadCount(),
          { count: Math.max(0, previousCount.count - 1) },
        );
      }

      if (wasUnread && projectId) {
        adjustProjectUnreadNotificationCount(queryClient, projectId, -1);
      }

      return { previousLists, previousCount, previousProjects };
    },
    onError: (_error, _notificationId, context) => {
      context?.previousLists.forEach(([queryKey, value]) =>
        queryClient.setQueryData(queryKey, value),
      );
      if (context?.previousCount) {
        queryClient.setQueryData(
          notificationQueryKeys.unreadCount(),
          context.previousCount,
        );
      }
      if (context?.previousProjects) {
        queryClient.setQueryData(
          projectQueryKeys.all,
          context.previousProjects,
        );
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.all,
      });
    },
  });
}
